
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/components/chat/types/conversation";

interface UseConversationDeleteProps {
  currentThreadId: string | null;
  onCreateNewConversation: () => void;
  forceRefresh: () => void;
}

export const useConversationDelete = ({
  currentThreadId,
  onCreateNewConversation,
  forceRefresh
}: UseConversationDeleteProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);
  const { toast } = useToast();

  const handleDeleteClick = (conversation: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!conversationToDelete) return;
    
    try {
      try {
        await supabase.functions.invoke("openai-assistant", {
          body: { 
            action: "delete_thread",
            threadId: conversationToDelete.thread_id
          }
        });
      } catch (apiError) {
        console.warn("Could not delete thread in OpenAI API. Continuing with database deletion.", apiError);
      }

      const { error } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('thread_id', conversationToDelete.thread_id);

      if (error) throw error;
      
      // Force refresh to get updated conversation list
      forceRefresh();
      
      toast({
        title: "Samtale slettet",
        description: "Samtalen er nå permanent slettet.",
      });
      
      if (currentThreadId === conversationToDelete.thread_id) {
        onCreateNewConversation();
      }
    } catch (err) {
      console.error("Feil ved sletting av samtale:", err);
      toast({
        variant: "destructive",
        title: "Feil ved sletting",
        description: "Kunne ikke slette samtalen. Prøv igjen senere."
      });
    } finally {
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  return {
    deleteDialogOpen,
    conversationToDelete,
    handleDeleteClick,
    handleConfirmDelete,
    closeDeleteDialog
  };
};
